import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { RewardConfigRarity } from '@sorare/core/src/__generated__/globalTypes';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { glossary } from '@sorare/core/src/lib/glossary';
import type { unitMapping } from '@sorare/core/src/lib/style';

import type { ShardRewards_cardShardRewardConfig } from './__generated__/index.graphql';

type Props = {
  shardRewardConfigs: ShardRewards_cardShardRewardConfig[];
  size: 56 | 32 | 24 | 16;
  iconSize: keyof typeof unitMapping;
  displayLabels: boolean;
  showNumber: boolean;
};

export const ShardRewards = ({
  shardRewardConfigs,
  size,
  iconSize,
  displayLabels,
  showNumber,
}: Props) => {
  const blockchainShardRewardConfigs = shardRewardConfigs.filter(
    ({ configRarity }) => configRarity !== RewardConfigRarity.common
  );
  const commonShardRewardConfigs = shardRewardConfigs.filter(
    ({ configRarity }) => configRarity === RewardConfigRarity.common
  );

  const blockchainShardTotalCount = blockchainShardRewardConfigs.reduce(
    (acc, rewardConfig) => acc + rewardConfig.quantity,
    0
  );

  const commonShardTotalCount = commonShardRewardConfigs.reduce(
    (acc, rewardConfig) => acc + rewardConfig.quantity,
    0
  );

  if (blockchainShardTotalCount > 0) {
    return (
      <Vertical gap={0.5}>
        <StatDisplay
          key="shard"
          size={size}
          icon={<Shards size={iconSize} color="var(--c-green-400)" />}
          rotateIcon={false}
          label={
            !!displayLabels && (
              <BodyS>
                <FormattedMessage {...glossary.essence} />
              </BodyS>
            )
          }
        >
          {!!showNumber && blockchainShardTotalCount}
        </StatDisplay>
        {commonShardTotalCount > 0 && !!displayLabels && (
          <LabelS color="var(--c-nd-700)">
            (+{commonShardTotalCount}{' '}
            <FormattedMessage {...glossary.commonEssence} />)
          </LabelS>
        )}
      </Vertical>
    );
  }

  return (
    <StatDisplay
      key="shard"
      size={size}
      icon={<Shards size={iconSize} color="var(--c-white)" />}
      rotateIcon={false}
      label={
        !!displayLabels && (
          <BodyS>
            <FormattedMessage {...glossary.commonEssence} />
          </BodyS>
        )
      }
    >
      {!!showNumber && commonShardTotalCount}
    </StatDisplay>
  );
};

ShardRewards.fragments = {
  cardShardRewardConfig: gql`
    fragment ShardRewards_cardShardRewardConfig on CardShardRewardConfig {
      id
      configRarity
      quantity
    }
  ` as TypedDocumentNode<ShardRewards_cardShardRewardConfig>,
};
